import { OUTBOX_FLUSH_MESSAGE, OUTBOX_SYNC_TAG } from './constants'
import { flushOutbox } from './outbox'

const INTERVAL_MS = 30_000

type SyncRegistration = ServiceWorkerRegistration & {
  sync?: { register(tag: string): Promise<void> }
}

/** Ask the browser to wake us when signal returns (Android Chrome only; a no-op elsewhere). */
export async function requestBackgroundSync() {
  try {
    if (!('serviceWorker' in navigator)) return
    const reg = (await navigator.serviceWorker.ready) as SyncRegistration
    await reg.sync?.register(OUTBOX_SYNC_TAG)
  } catch {
    // Unsupported or denied: the triggers below still cover it.
  }
}

/** Ask the browser not to evict drafts and photos when storage runs low. */
export async function requestPersistentStorage() {
  try {
    if (navigator.storage?.persisted && !(await navigator.storage.persisted())) {
      await navigator.storage.persist()
    }
  } catch {
    // Not supported: nothing else to do.
  }
}

/**
 * Flush the outbox whenever there is a chance it will work: on start, when the connection comes
 * back, when the app regains focus, every 30 seconds, and when the service worker relays a
 * Background Sync. Returns a cleanup function.
 */
export function startOutboxSync(userId: string) {
  const flush = () => void flushOutbox({ userId })
  const onVisible = () => {
    if (document.visibilityState === 'visible') flush()
  }
  const onMessage = (event: MessageEvent) => {
    if (event.data?.type === OUTBOX_FLUSH_MESSAGE) flush()
  }

  window.addEventListener('online', flush)
  window.addEventListener('focus', flush)
  document.addEventListener('visibilitychange', onVisible)
  navigator.serviceWorker?.addEventListener('message', onMessage)
  const timer = window.setInterval(flush, INTERVAL_MS)
  flush()

  return () => {
    window.removeEventListener('online', flush)
    window.removeEventListener('focus', flush)
    document.removeEventListener('visibilitychange', onVisible)
    navigator.serviceWorker?.removeEventListener('message', onMessage)
    window.clearInterval(timer)
  }
}
